import { getGlobalStyles, GridProvider, StickyBarProvider, ThemeProvider } from '@bob/library'
import { ErrorBoundary } from '@sentry/gatsby'
import { AppContext, AppProviderContext } from '@websrc/App/AppContext'
import { CookieConsent } from '@websrc/App/Components/CookieConsent/CookieConsent'
import { DataLayer } from '@websrc/App/Components/DataLayer'
import { DataLayerBasketState } from '@websrc/App/Components/DataLayerBasketState'
import { LocaleCookie } from '@websrc/App/Components/LocaleCookie'
import { TopbarAlerts } from '@websrc/App/Components/TopbarAlerts/TopbarAlerts'
import { calcBgAndFontColor } from '@websrc/App/Helpers/calcBgAndFontColor'
import { useSentry } from '@websrc/App/Hooks/useSentry'
import { ErrorPage } from '@websrc/App/Pages/ErrorPage/ErrorPage'
import { FacebookPixel } from '@websrc/App/Tracking/FacebookPixel'
import { GoogleTagManager } from '@websrc/App/Tracking/GoogleTagManager'
import { LinkedInPixel } from '@websrc/App/Tracking/LinkedInPixel'
import { MarketingParams } from '@websrc/App/Tracking/MarketingParams'
import { Matomo } from '@websrc/App/Tracking/Matomo'
import { XLink } from '@websrc/App/XLink/XLink'
import { BasketStateProvider } from '@websrc/Basket/useBasketState'
import { CoralProductMetaContext, ProductMetaContext } from '@websrc/Basket/useProductMeta'
import MainTopbar from '@websrc/MainTopbar/MainTopbar'
import React, { ReactNode } from 'react'
import { Helmet } from 'react-helmet'

import { CoralTopbar } from './Components/CoralTopbar/CoralTopbar'
import { CoralGlobalFooter } from './Components/Footer/CoralGlobalFooter/CoralGlobalFooter'
import { SEO } from './Components/SEO'
import { SEOBlog } from './Components/SEOBlog'
import { SignUpVisibilityProvider } from './Hooks/useSignUpVisibility'
import { PinterestPixel } from './Tracking/PinterestPixel'
import { UET } from './Tracking/UET'

export interface AppProviderProps extends AppContext {
  children: ReactNode
}

export const MAIN_CONTENT_ID = 'mainContent'

// eslint-disable-next-line complexity, max-lines-per-function
export const AppProvider = ({
  pageName = '',
  siteSection = 'legoeducation',
  children,
  isMarketingPage,
  cookiePolicy,
  coralGlobalFooter,
  markets,
  productMetaState,
  coralProductMetaState,
  productDetails,
  topbar,
  coralTopbar,
  servicebar,
  topbarAlerts,
  hideMainTopbar,
  showCoralTopbar,
  hideFooter,
  hideCta,
  seo,
  seoBlog,
  otherLocaleUrls,
  colorTheme,
  matomoCallTrackPageView,
  ...appContext
}: AppProviderProps) => {
  useSentry()

  return (
    <AppProviderContext.Provider value={appContext}>
      <ErrorBoundary fallback={(props) => <ErrorPage {...props} />}>
        <ThemeProvider
          customTheme={{
            colorTheme: colorTheme
              ? calcBgAndFontColor(colorTheme)
              : calcBgAndFontColor('Corporate Blue'),
          }}
        >
          <Helmet>
            <style type={'text/css'}>{getGlobalStyles()}</style>
            <html dir={markets.currentMarket.isRtl ? 'rtl' : 'ltr'} />
            <link href={'/humans.txt'} rel={'author'} />
          </Helmet>
          <UET />
          <FacebookPixel />
          <LinkedInPixel />
          <GoogleTagManager />
          <PinterestPixel path={appContext.path} productDetails={productDetails} />
          <MarketingParams />
          <Matomo
            callTrackPageView={matomoCallTrackPageView}
            coralProductMetaState={coralProductMetaState}
            locale={appContext.locale}
            pageTemplate={appContext.matomoPageTemplate}
            path={appContext.path}
            productCategory={appContext.matomoProductCategory}
            productDetails={productDetails}
            productMetaState={productMetaState}
            title={seo?.title ?? ''}
          />
          <DataLayer.Provider>
            <DataLayer.Set
              debugMessage={'AppProvider'}
              values={{
                site: {
                  section: siteSection,
                },
                page: {
                  locale: appContext.locale,
                },
                page_name: pageName,
                page_type: appContext.pageType,
                page_section: siteSection,
              }}
            />
            {seo && <SEO otherLocaleUrls={otherLocaleUrls} {...seo} />}
            {seoBlog && <SEOBlog otherLocaleUrls={otherLocaleUrls} {...seoBlog} />}
            <XLink />
            <CoralProductMetaContext.Provider value={coralProductMetaState}>
              <ProductMetaContext.Provider value={productMetaState}>
                <BasketStateProvider enable={Boolean(topbar?.shopping_cart)}>
                  <DataLayerBasketState />
                  <StickyBarProvider>
                    <GridProvider>
                      <SignUpVisibilityProvider>
                        <div id={'stickyBarPortal'}>
                          {!hideMainTopbar && !showCoralTopbar && (
                            <>
                              {!appContext.hideTopbarAlerts && (
                                <TopbarAlerts alerts={topbarAlerts} isSticky={false} />
                              )}

                              <MainTopbar
                                markets={markets}
                                onlyShowLogo={appContext.topBarOnlyShowsLogo}
                                servicebar={servicebar}
                                topbar={topbar}
                              />
                            </>
                          )}
                          {!hideMainTopbar && showCoralTopbar && (
                            <CoralTopbar topbar={coralTopbar} />
                          )}
                        </div>

                        <article id={MAIN_CONTENT_ID}>{children}</article>

                        {!hideFooter && coralGlobalFooter && (
                          <CoralGlobalFooter
                            cookiePolicy={cookiePolicy}
                            copyright={coralGlobalFooter.copyright}
                            cta_section={coralGlobalFooter.cta_section}
                            enable_global_footer={coralGlobalFooter.enable_global_footer}
                            hideCta={hideCta}
                            isMarketingPage={isMarketingPage}
                            link_section={coralGlobalFooter.link_section}
                            share_section={coralGlobalFooter.share_section}
                            simple_footer={coralGlobalFooter.simple_footer}
                            terms_policy={coralGlobalFooter.terms_policy}
                          />
                        )}
                      </SignUpVisibilityProvider>
                    </GridProvider>
                  </StickyBarProvider>
                </BasketStateProvider>
              </ProductMetaContext.Provider>
            </CoralProductMetaContext.Provider>

            {!appContext.disableCookies && (
              <>
                <CookieConsent cookiePolicy={cookiePolicy} />
                <LocaleCookie />
              </>
            )}
          </DataLayer.Provider>
        </ThemeProvider>
      </ErrorBoundary>
    </AppProviderContext.Provider>
  )
}
